/**
 * service-identity admin client for the web-staff security pages.
 *
 * Mirrors the UsersController (`/v1/identity/users`) and RolesController
 * (`/v1/identity/roles`) that live in `apps/service-identity/src/modules/`.
 *
 * All routes require the caller to hold the corresponding RBAC permission:
 *   Identity/readUser   — list + get
 *   Identity/updateUser — patch (status, displayName, …)
 *   Identity/grantRole  — POST :id/roles
 *   Identity/revokeRole — DELETE :id/roles/:roleId
 *   Identity/readRole   — list roles + permissions
 *
 * The dev-superadmin-role receives all five via
 * `prisma/identity/db-migrations/20260522_b3_security_admin_perms.sql`.
 */
import { env } from '../env';
import { request } from './client';

const baseUrl = (): string => env.identityBaseUrl;

// ---- User types ----

export type UserStatus = 'ACTIVE' | 'DISABLED' | 'LOCKED' | 'PENDING';

export interface UserDto {
  readonly id: string;
  readonly email: string;
  readonly phone: string | null;
  readonly displayName: string;
  readonly operatorId: number;
  readonly status: UserStatus;
  readonly isPlatformAdmin: boolean;
  readonly lastLoginAt: string | null;
  readonly passwordChangedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UpdateUserArgs {
  readonly email?: string;
  readonly phone?: string | null;
  readonly displayName?: string;
  readonly operatorId?: number;
  readonly status?: UserStatus;
  readonly isPlatformAdmin?: boolean;
}

// ---- Role types ----

export interface RoleDto {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ---- User API ----

/** List all users accessible to the caller (operator-scoped unless platform admin). */
export async function listUsers(): Promise<readonly UserDto[]> {
  return request<readonly UserDto[]>(baseUrl(), '/v1/identity/users', { method: 'GET' });
}

/** Get a single user by id. */
export async function getUser(id: string): Promise<UserDto> {
  return request<UserDto>(baseUrl(), `/v1/identity/users/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/** Patch user fields. Used for enable/disable (status) and displayName edits. */
export async function updateUser(id: string, patch: UpdateUserArgs): Promise<UserDto> {
  return request<UserDto>(baseUrl(), `/v1/identity/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
  });
}

/** Disable a user account (set status = DISABLED). */
export async function disableUser(id: string): Promise<UserDto> {
  return updateUser(id, { status: 'DISABLED' });
}

/** Re-enable a disabled user account (set status = ACTIVE). */
export async function enableUser(id: string): Promise<UserDto> {
  return updateUser(id, { status: 'ACTIVE' });
}

/** Grant a role to a user. Idempotent. Forces the user's sessions to expire so
 *  they pick up the new permissions claim on next login. */
export async function grantRole(userId: string, roleId: string): Promise<void> {
  await request<unknown>(
    baseUrl(),
    `/v1/identity/users/${encodeURIComponent(userId)}/roles`,
    { method: 'POST', body: { roleId } },
  );
}

/** Revoke a role from a user. Idempotent. Forces session expiry. */
export async function revokeRole(userId: string, roleId: string): Promise<void> {
  await request<unknown>(
    baseUrl(),
    `/v1/identity/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleId)}`,
    { method: 'DELETE' },
  );
}

// ---- Role API ----

/** List all roles. */
export async function listRoles(): Promise<readonly RoleDto[]> {
  return request<readonly RoleDto[]>(baseUrl(), '/v1/identity/roles', { method: 'GET' });
}

/** Get a single role by id. */
export async function getRole(id: string): Promise<RoleDto> {
  return request<RoleDto>(
    baseUrl(),
    `/v1/identity/roles/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

/** List permissions (strings) granted to a role. */
export async function listRolePermissions(roleId: string): Promise<readonly string[]> {
  return request<readonly string[]>(
    baseUrl(),
    `/v1/identity/roles/${encodeURIComponent(roleId)}/permissions`,
    { method: 'GET' },
  );
}

// ---- User CRUD (admin) ----

export interface CreateUserArgs {
  readonly email: string;
  readonly password?: string;
  readonly phone?: string;
  readonly displayName: string;
  readonly operatorId: number;
  readonly isPlatformAdmin?: boolean;
  readonly roleIds?: readonly string[];
}

/** Create a user via POST /v1/identity/users. Requires Identity/createUser. */
export async function createUser(args: CreateUserArgs): Promise<UserDto> {
  return request<UserDto>(baseUrl(), '/v1/identity/users', {
    method: 'POST',
    body: args,
  });
}

export interface UserRoleAssignmentDto {
  readonly roleId: string;
  readonly grantedBy: string;
  readonly grantedAt: string;
}

/** List role grants for a user. Requires Identity/readUser. */
export async function listUserRoles(userId: string): Promise<readonly UserRoleAssignmentDto[]> {
  return request<readonly UserRoleAssignmentDto[]>(
    baseUrl(),
    `/v1/identity/users/${encodeURIComponent(userId)}/roles`,
    { method: 'GET' },
  );
}

// ---- Role CRUD (admin) ----

export interface CreateRoleArgs {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly permissions?: readonly string[];
}

export interface UpdateRoleArgs {
  readonly name?: string;
  readonly description?: string | null;
  readonly permissions?: readonly string[];
}

/** Create a non-system role. Requires Identity/createRole. */
export async function createRole(args: CreateRoleArgs): Promise<RoleDto> {
  return request<RoleDto>(baseUrl(), '/v1/identity/roles', {
    method: 'POST',
    body: args,
  });
}

/** Update a non-system role. Requires Identity/updateRole. */
export async function updateRole(roleId: string, patch: UpdateRoleArgs): Promise<RoleDto> {
  return request<RoleDto>(
    baseUrl(),
    `/v1/identity/roles/${encodeURIComponent(roleId)}`,
    { method: 'PATCH', body: patch },
  );
}

// ---- Permission catalog (drives the role-edit checkbox tree) ----

export interface PermissionRouteDto {
  readonly method: string;
  readonly path: string;
  readonly file: string;
  readonly decorators: readonly string[];
}

export interface PermissionActionDto {
  readonly action: string;
  readonly permission: string;
  readonly routes: readonly PermissionRouteDto[];
  readonly inCode: boolean;
  readonly inSeed: boolean;
}

export interface PermissionModuleDto {
  readonly module: string;
  readonly actions: readonly PermissionActionDto[];
}

export interface PermissionCatalogDto {
  readonly modules: readonly PermissionModuleDto[];
  readonly generatedAt: string;
}

/**
 * Fetch the merged code-scan + seeded permission catalog.
 *
 * Permission: `Admin/readPermissionCatalog`. Granted to platform-admin and
 * dev-superadmin-role via 20260513_settings_admin_perms.sql.
 */
export async function getPermissionCatalog(): Promise<PermissionCatalogDto> {
  return request<PermissionCatalogDto>(
    baseUrl(),
    '/v1/identity/permissions/catalog',
    { method: 'GET' },
  );
}

// ---- Field-permission editor (F-387) ----

export interface RoleFieldGrant {
  readonly field: string;
  readonly canView: boolean;
  readonly canEdit: boolean;
  readonly canAdd: boolean;
}

export interface RoleFieldDto extends RoleFieldGrant {
  readonly roleId: string;
  readonly model: string;
  readonly updatedBy: string | null;
  readonly reason: string | null;
  readonly updatedAt: string;
}

/** List per-field grants for a role (optionally scoped to one model). */
export async function listRoleFields(
  roleId: string,
  model?: string,
): Promise<readonly RoleFieldDto[]> {
  const query = model ? { model } : {};
  return request<readonly RoleFieldDto[]>(
    baseUrl(),
    `/v1/identity/roles/${encodeURIComponent(roleId)}/fields`,
    { method: 'GET', query },
  );
}

/** Replace-all per-field grants for a (roleId, model) pair. */
export async function replaceRoleFields(
  roleId: string,
  body: { model: string; reason?: string; grants: readonly RoleFieldGrant[] },
): Promise<readonly RoleFieldDto[]> {
  return request<readonly RoleFieldDto[]>(
    baseUrl(),
    `/v1/identity/roles/${encodeURIComponent(roleId)}/fields`,
    { method: 'PUT', body },
  );
}

// ---- Identity stats (security dashboard) ----

export interface IdentityDashboardStats {
  readonly activeUsers: number;
  readonly disabledUsers: number;
  readonly lockedAccounts: number;
  readonly pendingUsers: number;
  readonly platformAdmins: number;
  readonly totalUsers: number;
  readonly totalRoles: number;
  readonly activeSessions: number;
}

/** Identity-owned KPI counters for the staff Security dashboard. */
export async function getIdentityDashboardStats(): Promise<IdentityDashboardStats> {
  return request<IdentityDashboardStats>(baseUrl(), '/v1/identity/stats/dashboard', {
    method: 'GET',
  });
}

// ---------------------------------------------------------------------------
// Self-service security surface — drives the driver-portal Security tab.
// No special RBAC permission required; the endpoint resolves the caller via
// the bearer token's `userId` claim.
// ---------------------------------------------------------------------------

export interface MySecurityResponse {
  readonly email: string;
  readonly emailVerified: boolean;
  readonly phone: string | null;
  readonly phoneVerified: boolean;
  readonly lastLoginAt: string | null;
  readonly passwordChangedAt: string | null;
   
  readonly activeSessionCount: number;
}

export async function getMySecurity(): Promise<MySecurityResponse> {
  return request<MySecurityResponse>(baseUrl(), '/v1/auth/me/security', {
    method: 'GET',
  });
}

export interface SignOutEverywhereResponse {
   
  readonly revoked: number;
}

export async function signOutEverywhere(): Promise<SignOutEverywhereResponse> {
  return request<SignOutEverywhereResponse>(baseUrl(), '/v1/auth/me/sign-out-everywhere', {
    method: 'POST',
    body: {},
    // Don't let the 401 → refresh path nuke our token if this call itself
    // races a session-revoked state — the caller intends to log out anyway.
    suppressAuthReset: true,
  });
}
